<template>
    <div class="col-sm-12 nopads mt-2">
        <div class="search-form px-3">
            <div class="col-sm-12 search-form-title">
                <font-awesome-icon icon="search" />
                {{ $t('common.search') }}
            </div>
            <div class="no-margin">
                <div class="row">
                    <div class="col-sm-6 px-2">
                        <span class="span-title">
                            {{ $t('ins_report.from_date') }}
                        </span>
                        <date-time-picker
                            v-model:date="fromDate"
                            class="routa-input"
                            format="dd.MM.yyyy HH:mm"
                            :enable-time-picker="true"
                            :set-time-button-visible="true"
                            :placeholder="$t('areas.set_work_time')"
                        />
                    </div>

                    <div class="col-sm-6 px-2">
                        <span class="span-title">
                            {{ $t('ins_report.to_date') }}
                        </span>
                        <date-time-picker
                            v-model:date="toDate"
                            class="routa-input"
                            format="dd.MM.yyyy HH:mm"
                            :enable-time-picker="true"
                            :set-time-button-visible="true"
                            :placeholder="$t('areas.set_work_time')"
                        />
                    </div>
                </div>
                <!-- Observer's contract  filtering -->
                <div
                    v-if="isObserver"
                    class="row"
                >
                    <div class="col-sm-3 px-2 hidden">
                        <div class="nopads">
                            <span class="span-title">{{ $t('observations.contract_collection') }}</span>
                            <select
                                id="contractCollection"
                                v-model="selectedContractCollection"
                                :disabled="!contractCollectionOptions || contractCollectionOptions.length === 0"
                                class="form-select form-select-sm"
                            >
                                <option :value="null">
                                    {{ $t('trip_list.search_all') }}
                                </option>
                                <option
                                    v-for="item in contractCollectionOptions"
                                    :key="item.id"
                                    :value="item"
                                >
                                    {{ item.name }}
                                </option>
                            </select>
                        </div>
                    </div>
                    <div
                        class="col-sm-4"
                        style="margin-top: .35em"
                    >
                        <open-contract-filter
                            ref="contracts"
                            :to="toDate"
                            :from="fromDate"
                            :contract-collection="selectedContractCollection"
                            :show-company="false"
                            :show-only-my-contracts-option="true"
                            :show-title="true"
                            :contract="preSelectedContract"
                            @loading="loading = true"
                            @loading-completed="loading = false"
                            @contract-selected="setContract"
                            @contracts-fetched="setContractCollections"
                        />
                    </div>

                    <div class="col-sm-4">
                        <contract-order-multiselect
                            :contract-ids="contractIds"
                            :show-info="true"
                            :show-executor-text="true"
                            @orders-selected="setOrders"
                        />
                    </div>

                    <!-- Contractor -->
                    <div class="col-sm-4">
                        <span class="span-title">{{ $t('trip_list.search_with_company') }}</span>
                        <multiselect
                            v-model="companyParam"
                            class="multiselect_routa"
                            :placeholder="companyPlaceholder"
                            :deselect-label="$t('common.remove_selection')"
                            :select-label="$t('common.select')"
                            :close-on-select="false"
                            :disabled="!areaGroupsLoaded || companyOptions.length === 0"
                            label="name"
                            track-by="name"
                            :searchable="true"
                            :multiple="true"
                            :internal-search="true"
                            :selected-label="$t('common.selected')"
                            :options="companyOptions"
                        >
                            <template
                                slot="singleLabel"
                                slot-scope="props"
                            >
                                <span class="option__desc">
                                    <span class="option__title">{{ props.option.name }}</span>
                                </span>
                            </template>
                            <template
                                slot="option"
                                slot-scope="props"
                            >
                                <div class="option__desc">
                                    <span class="option__title multiselect_detail_block">{{ props.option.name }}</span>
                                </div>
                            </template>
                            <span slot="noResult">{{ $t('common.not_found') }}</span>
                            <span slot="noOptions">{{ $t('common.no_results') }}</span>
                        </multiselect>
                    </div>
                </div>

                <!-- Map viewer's contract  filtering -->
                <div
                    v-else
                    class="row"
                >
                    <div
                        class="col-sm-6"
                        style="margin-top: .35em"
                    >
                        <open-contract-filter
                            ref="contracts"
                            :to="toDate"
                            :from="fromDate"
                            :contract-collection="selectedContractCollection"
                            :show-company="false"
                            :show-only-my-contracts-option="true"
                            :show-title="true"
                            :contract="preSelectedContract"
                            @loading="loading = true"
                            @loading-completed="loading = false"
                            @contract-selected="setContract"
                            @contracts-fetched="setContractCollections"
                        />
                    </div>

                    <div class="col-sm-6">
                        <contract-order-multiselect
                            :contract-ids="contractIds"
                            :show-info="true"
                            :show-executor-text="true"
                            @orders-selected="setOrders"
                        />
                    </div>
                </div>

                <!-- Search area -->
                <div class="row">
                    <div
                        class="col-sm-5"
                        style="padding-top: .1em"
                    >
                        <span class="span-title">{{ $t('trip_list.area') }}</span>
                        <select
                            id="searchAreaInput"
                            v-model="selectedAreaGroup"
                            :disabled="!areaGroupsLoaded || mapSelectedArea"
                            class="form-select form-select-sm no-margin"
                        >
                            <option :value="null">
                                {{ $t('trip_list.search_all') }}
                            </option>
                            <option
                                v-for="item in areaGroups"
                                :key="item.id"
                                :value="item.value"
                            >
                                {{ item.text }}
                            </option>
                        </select>
                    </div>
                    <div class="col-sm-3">
                        <span class="span-title">{{ $t('area_report.select_area') }}</span>
                        <button
                            class="btn btn-sm btn-outline-info form-button"
                            :disabled="isAreaSelected"
                            :class="{'primary' : mapSelectedArea, 'info' : !mapSelectedArea}"
                            style="margin-top: 1px"
                            @click="openModalMap"
                        >
                            {{ $t('trip_list.select_area_map') }}
                        </button>
                        <span
                            v-if="mapDrawnAreas"
                            class="pl-2 hint-text"
                        >{{ $t('trip_list.area_selected') }}</span>
                    </div>
                    <!-- Pricing model -->
                    <div
                        v-if="isObserver"
                        class="col-sm-4"
                    >
                        <pricing-model-filter v-model="pricingModel" />
                    </div>
                </div>
                <div
                    v-if="isObserver"
                    class="row"
                >
                    <!-- Vehicle -->
                    <div class="col-sm-6">
                        <span class="span-title">{{ $t('trip_list.vehicle') }}</span>
                        <multiselect
                            v-model="vehicle"
                            class="multiselect_routa"
                            :placeholder="$t('trip_list.select_vehicle')"
                            :deselect-label="$t('common.remove_selection')"
                            :select-label="$t('common.select')"
                            :close-on-select="true"
                            label="name"
                            track-by="name"
                            :searchable="true"
                            :internal-search="true"
                            :selected-label="$t('common.selected')"
                            :options="vehicleOptions"
                        >
                            <template
                                slot="singleLabel"
                                slot-scope="props"
                            >
                                <span class="option__desc">
                                    <span class="option__title">{{ props.option.name }}</span>
                                </span>
                            </template>
                            <template
                                slot="option"
                                slot-scope="props"
                            >
                                <div class="option__desc">
                                    <span class="option__title multiselect_detail_block">{{
                                        props.option.name
                                    }}</span>
                                </div>
                            </template>
                            <span slot="noResult">{{ $t('common.not_found') }}</span>
                            <span slot="noOptions">{{ $t('common.no_results') }}</span>
                        </multiselect>
                    </div>
                    <!-- User -->
                    <div
                        v-if="isObserver"
                        class="col-sm-6"
                    >
                        <span class="span-title">{{ $t('trip_list.search_with_username') }}</span>
                        <multiselect
                            v-model="userParam"
                            class="multiselect_routa"
                            :placeholder="$t('work_time.select_person')"
                            :deselect-label="$t('common.remove_selection')"
                            :select-label="$t('common.select')"
                            :close-on-select="true"
                            label="name"
                            track-by="name"
                            :searchable="true"
                            :internal-search="true"
                            :selected-label="$t('common.selected')"
                            :options="userOptions"
                        >
                            <template
                                slot="singleLabel"
                                slot-scope="props"
                            >
                                <span class="option__desc">
                                    <span class="option__title">{{ props.option.name }}</span>
                                </span>
                            </template>
                            <template
                                slot="option"
                                slot-scope="props"
                            >
                                <div class="option__desc">
                                    <span class="option__title multiselect_detail_block">{{ props.option.name }}</span>
                                </div>
                            </template>
                            <span slot="noResult">{{ $t('common.not_found') }}</span>
                            <span slot="noOptions">{{ $t('common.no_results') }}</span>
                        </multiselect>
                    </div>
                </div>
                <div class="row">
                    <div class="col-sm-6">
                        <span class="span-title">{{ $t('trip_list.search_with_road_name') }}</span>
                        <input
                            id="roadNameInput"
                            v-model="roadName"
                            type="text"
                            class="form-control form-control-sm no-margin"
                            :placeholder="$t('trip_list.search_with_road_name')"
                        >
                    </div>
                    <div class="col-sm-6">
                        <span class="span-title">{{ $t('trip_list.mode') }}</span>
                            <input
                                id="tripMode"
                                class="form-check-input mt-1"
                                type="checkbox"
                                v-model="onlyWorkMode"
                            />
                            <label class="form-check-label mx-2" for="tripMode">
                                {{ $t('trip.only_work_mode') }}
                            </label>
                    </div>
                </div>
                <div class="row">
                    <div class="col-sm-4">
                        <span class="span-title">{{ $t('trip_list.search_with_road_num') }}</span>
                        <input
                            id="roadInput"
                            v-model="roadNumber"
                            type="number"
                            class="form-control form-control-sm no-margin"
                            :placeholder="$t('trip_list.search_with_road_num')"
                        >
                    </div>
                    <div class="col-sm-4">
                        <span class="span-title">{{ $t('map.road_section_start') }}</span>
                        <input
                            id="roadSecFrom"
                            v-model="roadSecFrom"
                            type="number"
                            class="form-control form-control-sm no-margin"
                            :placeholder="$t('trip_list.road_sec_from')"
                        >
                    </div>
                    <div class="col-sm-4">
                        <span class="span-title">{{ $t('map.road_section_end') }}</span>
                        <input
                            id="roadSecTo"
                            v-model="roadSecTo"
                            type="number"
                            class="form-control form-control-sm no-margin"
                            :placeholder="$t('trip_list.road_sec_to')"
                        >
                    </div>
                </div>
                <div class="col-sm-12 button-container">
                    <button
                        class="btn btn-danger form-button"
                        @click.stop="onReset"
                    >
                        {{ $t('ins_report.clear') }}
                    </button>
                    <button
                        class="btn btn-info form-button"
                        :disabled="!contract || !contract.id"
                        @click.stop="onSubmitToday"
                    >
                        {{ $t('trip.todays_trips') }}
                    </button>
                    <button
                        type="submit"
                        class="btn btn-primary form-button"
                        @click.stop="onSubmit"
                    >
                        {{ $t('ins_report.search') }}
                    </button>
                </div>
            </div>
        </div>
        <!--select area on map modal-->
        <base-modal
            v-if="showMapModal"
            size="lg"
            hide-header
            hide-footer
            @close="showMapModal = false"
        >
            <div
                class="col-12"
                @click.stop=""
            >
                <div
                    class="coordinate-map"
                    style="height:80vh; position: relative"
                >
                    <map-container
                        ref="map"
                        :find-user="true"
                        :is-editing="isEditing"
                        :center="mapCenter"
                        :menu-enabled="false"
                        @on-zoom-level-change="onZoomLevelChange"
                        @on-map-changed="mapReady"
                        @on-map-clicked="mapClicked"
                        @edit-area-event="editAreaEvent"
                    />
                </div>
                <div class="col-sm-12 text-end">
                    <button
                        class="btn btn-danger form-button"
                        @click.stop="clearPolygon"
                    >
                        {{ $t('ins_report.clear') }}
                    </button>
                    <button
                        class="btn btn-primary form-button"
                        @click.stop="backFromModal"
                    >
                        {{ $t('common.back') }}
                    </button>
                    <button
                        class="btn btn-success form-button"
                        @click.stop="closeModalMap"
                    >
                        {{ $t('common.save') }}
                    </button>
                </div>
                <div
                    v-if="loading"
                    id="loader"
                    class="spinner"
                />
            </div>
        </base-modal>
        <div
            v-if="loading"
            id="loader"
            class="spinner"
        />
    </div>
</template>

<script>
import { restApi } from '../mixins/RestApiMixin'
import Multiselect from 'vue-multiselect'
import { timeUtils } from '../mixins/TimeUtils'
import { orderMixin } from "../mixins/OrderMixin";
import PricingModelFilter from "../pricingmodels/PricingModelFilter";
import { areaHelper } from "@/components/mixins/AreaMixin";
import { contractHelper } from "@/components/mixins/ContractMixin";
import MapContainer from "@/components/map/MapContainer.vue";
import BaseModal from "@/components/BaseModal.vue";
import OpenContractFilter from "@/components/contract/OpenContractFilter.vue";
import { mapHelper } from "@/components/mixins/MapMixin";
import ContractOrderMultiselect from "@/components/order/ContractOrderMultiselect.vue";
import DateTimePicker from "@/components/common/DateTimePicker";

export default {
    name: 'TripSearch',
    components: {
        ContractOrderMultiselect,
        OpenContractFilter,
        BaseModal, MapContainer, PricingModelFilter,
        Multiselect,
        DateTimePicker
    },
    mixins: [restApi, timeUtils, orderMixin, areaHelper, contractHelper, mapHelper],
    props: {
        user: {
            type: Object,
            default: null
        },
        equipment: {
            type: Object,
            default: null
        },
        preSelectedContract: {
            type: Object,
            default: null
        },
        isObserver: {
            type: Boolean,
            default: false
        }
    },
    data: function () {
        return {
            advancedSearch: false,
            areaGroupObjects: [],
            areaGroups: [],
            selectedAreaGroup: null,
            userParam: null,
            companyParam: [],
            vehicle: null,
            fromDate: '',
            toDate: '',
            contract: null,
            contractIds: [],
            customer: null,
            contractor: null,
            pricingModel: null,
            order: null,
            roadSecFrom: '',
            roadSecTo: '',
            roadNumber: '',
            roadName: '',
            userOptions: [],
            companyOptions: [],
            vehicleOptions: [],
            contractCollectionOptions: [],
            orders: [],
            results: [],
            usersLoaded: false,
            areaGroupsLoaded: false,
            vehiclesLoaded: false,
            loading: false,
            companyPlaceholder: '',
            selectedAreaTypes: [],
            selectedContractCollection: null,
            newPolygonId: -1,
            equipmentUpdate: this.jsonToEquipment(this.equipment),
            mapCenter: null,
            isEditing: false,
            newAreaName: null,
            showMapModal: false,
            mapDrawnAreas: null,
            mapSelectedArea: this.updateSelected,
            areaSelected: false,
            drawnAreas: null,
            areaCleared: false,
            onlyWorkMode: false
        }
    },
    computed: {
        updateSelected: function () {
            return this.mapDrawnAreas === null ? false : this.mapDrawnAreas.length >= 4;
        },

        isAreaSelected: function () {
            return this.selectedAreaGroup !== null
        }
    },

    watch: {
        mapDrawnAreas: function () {
            this.mapSelectedArea = this.updateSelected;
        },
        companyParam: function () {
            if (this.contract) {
                this.updateListOptions()
            }
        },
        fromDate: function () {
            if (this.contract) {
                this.updateListOptions()
            }
        },
        toDate: function () {
            if (this.contract) {
                this.updateListOptions()
            }
        }
    },
    mounted: function () {
        this.loading = true
        if (!this.fromDate) {
            this.fromDate = this.preSelectedContract ? this.getDayStart() : this.getMonthStart()
        }
        if (this.preSelectedContract) {
            this.setContract(this.preSelectedContract)
        } else {
            this.updateListOptions()
        }
        this.getAreaGroups()
        this.enablePolygonDrawing()
        this.companyPlaceholder = this.$t('trip_list.search_select_contract_first')
    },
    methods: {

        mapReady() {
            this.redrawArea()
        },

        onZoomLevelChange(data) {
            this.$store.commit('storeMapZoomLevel', data)
        },

        backFromModal() {
            this.showMapModal = false
            this.areaCleared = false;
            if (this.mapDrawnAreas === null) {
                this.newPolygonId = -1
            }
        },

        closeModalMap() {
            if (this.areaCleared) {
                this.areaCleared = false;
                this.enablePolygonDrawing()
                this.mapDrawnAreas = null
                this.newPolygonId = -1
            } else {
                this.mapDrawnAreas = this.getPolygon();
            }
            this.showMapModal = false;
            this.$emit('areaGroupSelected', this.getSelectedAreaGroup())
            this.$emit('areaSelect', this.mapDrawnAreas)
        },

        disableAreaEditing() {
            if (this.isEditing) {
                this.isEditing = false
                this.polygonDrawing = true
                this.editingAreaEventFlag = false
                this.$refs.map.getMap().removeAnchorPoints()
            }
        },

        openModalMap() {
            // Init map center and open map
            if (this.getMapSettingsIn(['center'])) {
                this.mapCenter = this.getMapSettingsIn(['center'])
            } else if (this.user && this.user.company.location) {
                this.mapCenter = {
                    lon: this.user.company.location.x,
                    lat: this.user.company.location.y,
                }
            } else if (this.center) {
                this.mapCenter = this.center
            }
            this.showMapModal = true;
        },


        redrawArea: function () {
            this.$nextTick(() => {
                this.drawAreas(this.mapDrawnAreas)
            })
        },

        editingPolygon: function () {
            this.$refs.map.getMap().editArea(this.newPolygonId, this.OTHER)
            this.isEditing = true
            this.polygonDrawing = false
        },

        drawAreas: function (areas) {
            if (this.newPolygonId !== -1) {
                this.newPolygonId = -1
                areas.forEach(area => {
                    const poly = { lng: area[0], lat: area[1] }
                    this.addPointToPolygon(poly)
                })
            }
            else {
                this.disableAreaEditing()
            }
        },



        mapClicked: function (coord) {
            if (this.polygonDrawing && !this.isEditing) {
                this.addPointToPolygon(coord)
            }
        },


        editAreaEvent: function (id) {
            //This allows us to store the id of the polygon which is in focus
            //i.e. it was clicked on by the user
            if (!this.polygonDrawing) {
                this.editingAreaEventFlag = true
                this.newPolygonId = id
            }
        },

        clearPolygon: function () {
            this.$refs.map.getMap().removePolygon(this.newPolygonId, this.OTHER)
            this.enablePolygonDrawing()
        },

        addPointToPolygon: function (point) {
            if (this.$refs.map) {
            // If not exist yet, create one and add to map
                if (this.newPolygonId < 0) {
                    this.newPolygonId = Date.now()
                    this.$refs.map.getMap().newPolygon(this.newPolygonId, point.lat, point.lng, this.OTHER)
                } else {
                    this.$refs.map.getMap().addPointToPolygon(this.newPolygonId, point.lat, point.lng, this.OTHER)
                }
                this.areaCleared = false;
            }
        },

        enablePolygonDrawing: function () {
            this.newPolygonId = -1
            this.newAreaName = null
            this.newAreaSqm = null
            this.selectedAreaType = null
            this.polygonDrawing = true
            this.polygonDrawing = true
        },

        getSelectedAreaGroup: function () {
            if (this.selectedAreaGroup) {
                return this.areaGroupObjects.find(item => item.id === this.selectedAreaGroup)
            }
            return null
        },

        setContract: function (contract) {
            this.contract = contract
            this.updateContractIds()
            this.getContractCompanies()
            this.updateListOptions()
        },

        setOrders(orders) {
            this.orders = orders
            this.updateListOptions()
        },

        updateListOptions() {
            this.getUsers()
            this.getVehicles()
        },

        updateContractIds() {
            this.contractIds = []
            if (this.contract) {
                this.contractIds.push(this.contract.id)
            }
        },

        onReset: function () {
            this.roadNumber = ''
            this.roadName = ''
            this.roadSecFrom = ''
            this.roadSecTo = ''
            this.contract = null
            this.selectedContractCollection = null
            this.userParam = null
            this.pricingModel = null
            this.fromDate = this.getMonthStart()
            this.toDate = ''
            this.order = null
            this.orders = []
            this.companyParam = []
            this.selectedAreaGroup = null
            this.vehicle = null
            this.$emit('clearPolygon')
            this.enablePolygonDrawing()
            this.mapDrawnAreas = null;
            this.$refs.contracts.resetSelection()
            this.updateListOptions()
        },

        getOptionSearchParams() {
            let params = {
                from: this.getStartOfTheDay(this.fromDate, false),
                contract: this.contract?.id,
                orders: this.orders.map(order => order.id),
            }
            if (this.toDate) {
                params.to = this.getEndOfTheDay(this.toDate, false)
            }
            if (this.companyParam && this.companyParam.length > 0) {
                params.companies = this.companyParam.map(cParam => cParam.value)
            }
            return params
        },

        // Get users for dropdown menu
        getUsers: function () {
            this.loading = true
            this.usersLoaded = false
            if (this.contract) {
                let params = this.getOptionSearchParams()
                this.restFetchParams(this.tripUserUrl, params, this.handleUsers)
            } else {
                this.restFetch(this.userUrl, this.handleUsers)
            }
        },

        handleUsers: function (response) {
            this.loading = true
            if (response && response.data) {
                this.userOptions = response.data.map(function (user) {
                    return { value: user.id, name: user.last_name + ', ' + user.first_name }
                })
            }
            this.usersLoaded = true
            this.loading = this.isLoading()
        },

        // Get vehicles for dropdown menu
        getVehicles: function () {
            this.vehiclesLoaded = false
            if (this.contract) {
                let params = this.getOptionSearchParams()
                this.restFetchParams(this.tripVehicleUrl, params, this.handleVehicles)
            } else {
                this.restFetch(this.vehicleUrl, this.handleVehicles)
            }
        },

        handleVehicles: function (response) {
            if (response && response.data) {
                this.vehicleOptions = response.data.map(function (item) {
                    return {
                        value: item.id,
                        name: item.make + ' ' + item.vehicle_model + ' - ' + item.license_plate
                    }
                })
            }
            this.vehiclesLoaded = true
            this.loading = this.isLoading()
        },

        isLoading: function () {
            return !this.usersLoaded || !this.areaGroupsLoaded || !this.vehiclesLoaded
        },

        getContractCompanies: function () {
            this.companyOptions = []
            this.companyPlaceholder = this.$t('trip_list.search_select_contract_first')
            if (this.contract) {
                this.restFetchParams(this.contractCompanyUrl, { contract: this.contract.id }, this.handleContractCompanies)
            }
        },

        handleContractCompanies: function (response) {
            if (response && response.data) {
                this.companyPlaceholder = this.$t('')
                response.data.forEach(company => {
                    this.companyOptions.push({ name: company.name, value: company.id })
                })
                this.companyPlaceholder = this.$t('users.select_company')
            }
            this.loading = false
        },

        getAreaGroups: function () {
            this.loading = true
            this.restFetchParams(this.areaGroupUrl, { no_geom: 1 }, this.handleAreaGroupFetch)
        },

        handleAreaGroupFetch: function (response) {
            if (response.data) {
                this.areaGroupObjects = response.data
                response.data.forEach(areaGroup => {
                    this.areaGroups.push({ text: areaGroup.name, value: areaGroup.id })
                })
            }
            this.areaGroupsLoaded = true
            this.loading = this.isLoading()
        },

        getPolygon: function () {
            if (this.newPolygonId === -1) {
                return null
            }
            let original = this.$refs.map.getMap().getPolygonBoundaries(this.newPolygonId, this.OTHER)
            // Outer ring is enough, but close polyohn
            let result = original[0].map(item => [item['lng'], item['lat']])
            result.push([original[0][0].lng, original[0][0].lat])
            return result
        },

        onSubmitToday: function () {
            if (this.contract) {
                var params = {}
                const todayStart = new Date();
                todayStart.setHours(todayStart.getHours() - 24);
                params.contract = this.contract.id
                params.contractor = this.contract.contractor.id
                params.customer = this.contract.customer.id
                params.from = todayStart.toISOString()
                this.$emit('searchToday', params)
            }
        },

        onSubmit: function () {
            var params = {}
            if (this.selectedAreaGroup) {
                params.area_group = this.selectedAreaGroup
            }
            this.$emit('areaGroupSelected', this.getSelectedAreaGroup())
            if (this.roadNumber > 0) {
                params.road_num = this.roadNumber
            }
            if (this.roadName.trim()) {
                params.road_name = this.roadName
            }
            if (this.roadSecFrom > 0) {
                params.road_sec_from = this.roadSecFrom
            }
            if (this.roadSecTo > 0) {
                params.road_sec_to = this.roadSecTo
            }
            if (this.onlyWorkMode) {
                params.work_trips_only = true
            }
            if (this.contract) {
                params.contract = this.contract.id
                params.contractor = this.contract.contractor.id
                params.customer = this.contract.customer.id
            }
            if (this.order) {
                params.order = this.order.id
            }
            if (this.orders && this.orders.length > 0) {
                params.orders = this.orders.map(order => order.id)
            }
            if (this.pricingModel) {
                params.pricing_model = this.pricingModel.id
            }
            if (this.userParam) {
                params.user = this.userParam.value
            }
            if (this.fromDate) {
                params.from = this.fromDate
            }
            if (this.toDate) {
                params.to = this.toDate
            }
            if (this.companyParam && this.companyParam.length > 0) {
                params.companies = this.companyParam.map(cParam => cParam.value)
            }
            if (this.vehicle) {
                params.vehicle = this.vehicle.value
            }
            if (this.newPolygonId > 0) {
                const polygonObj = this.mapDrawnAreas;
                if (polygonObj !== null && polygonObj.length >= 4) {
                    params.polygon_search = "POLYGON((" + polygonObj.map(ob => ob.join(' ')) + "))";
                }
            }
            params.trip_notes = 1
            this.$emit('search', params)
        }
    }
}
</script>

<style scoped></style>
